import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import classNames from 'classnames'
import { connect } from 'react-redux'

import Loader from '../components/Loader'
import { Link } from 'react-router-dom'

import { ROUTE } from '../config'
import Icon from '../components/Icon'
import { generatePublicationLink, isWarningLightsPublication } from '../utils/publication'
import { getRootTopicForWarningLightsPublication } from '../utils/topics'

import '../styles/TopicsList.scss'
import DownloadIcon from '../svg/download'

// note: warning lights pub is checked with publicationSubType
const publicationsTypeToIconsMap = {
  'UG': 'quick-guide',
  'QRG': 'quick-guide',
  'OG': 'quick-guide',
  'MNT': 'maintenance',
  'OM': 'owners-manual',
  'NM': 'navigation-manual',
  'WTY': 'other-manuals',
  'RAD': 'folder',
  'VS': 'folder',
}

const TopicsList = ({
  topics,
  publications,
  emptyText = "No topics found",
  baseRoute,
  className,
  isDesktop = false,
  onClick,
  allTopics,
  contentIndex
}) => {
  const topicElements = useMemo(() => {
    const publicationsList = publications ? publications.toJS() : []
    let topicsList = topics
    let topicElements = []

    // group publications by publicationType + title (except UG and DEFAULT => unique per resourceKey)
    const groupedPubs = {}
    publicationsList.forEach(publication => {
      const publicationType = publication.publicationType
      const title = publication.title
      const resourceKey = publication.resourceKey
      if (publicationType === 'UG' || publicationType === 'DEFAULT' || !publicationType) {
        const key = `${publicationType || 'DEFAULT'}-${resourceKey}`
        groupedPubs[key] = [publication]
      } else {
        const key = `${publicationType}-${title}`
        if (!groupedPubs[key]) groupedPubs[key] = []
        groupedPubs[key].push(publication)
      }
    })

    // track which groups we already rendered (so we don't push duplicates for topics that belong to the same group)
    const renderedGroups = new Set()
    let publicationTypes = {}
    let index = 0

    topicsList.forEach((topic, key) => {
      const isFolder = topic.get('type') === 'folder'
      const isPublication = topic.get('type') === 'publication'
      const resourceKey = topic.get('resourceKey')
      const hasVideo = !!topic.get('video')
      const hasImage = false //TODO: check for images in topic

      let publicationType = ''
      if (isPublication) {
        const pubInfo = contentIndex && contentIndex[topic.get('id')]
        const isWarningLightsPub = isWarningLightsPublication(pubInfo)

        publicationType = pubInfo && pubInfo.publicationType
        const title = topic.get('title')

        let groupKey = ''
        if (publicationType === 'UG' || publicationType === 'DEFAULT' || !publicationType) {
          groupKey = `${publicationType || 'DEFAULT'}-${resourceKey}`
        } else {
          groupKey = `${publicationType}-${title}`
        }

        // if we've already rendered this group, skip (ensures one element per group)
        if (renderedGroups.has(groupKey)) {
          return true
        }
        renderedGroups.add(groupKey)

        let publicationIcon = isWarningLightsPub ? 'warning-lights' : (publicationsTypeToIconsMap[publicationType] || 'other-manuals')

        if (isWarningLightsPub) {
          // warning lights: compute root topic and render a single button that navigates to the root topic
          const warnLightsRootTopic = getRootTopicForWarningLightsPublication(allTopics, publications)
          const warnLink = `${baseRoute.split(ROUTE.BROWSE)[0]}${ROUTE.WARNING_LIGHTS}${ROUTE.TOPIC}/${warnLightsRootTopic.get('resourceKey')}`

          topicElements.push(
            <li key={key + 1}>
              <div className="item-inner">
                <div className="item-links">
                  <Link to={warnLink} className="nav-button view-button" onClick={() => { onClick && onClick(topic) }}>
                    View
                  </Link>
                </div>
                <div className="title-icon">
                  <div className="item-iconbar">
                    {hasVideo && <Icon type="list-film" />}
                    {hasImage && <Icon type="list-image" />}
                    {isPublication && <Icon type={publicationIcon} />}
                    {!isFolder && !isPublication && !hasVideo && !hasImage && <Icon type="list-topic" />}
                    {!isDesktop && isFolder && <Icon type="arrow-left" />}
                    {isDesktop && isFolder && <Icon type="folder" />}
                  </div>
                  <div className="item-title">
                    {title}
                  </div>
                </div>
              </div>
            </li>
          )
        } else {
          // normal publications: render one <li> per group with multiple navigation links (PDF / HTML)
          const pubsForGroup = groupedPubs[groupKey] || []

          topicElements.push(
            <li key={key + 1}>
              <div className="item-inner">
                <div className={`item-links ${!pubsForGroup?.[0]?.bodyPdf ? 'button-order' : ''}`}>
                  {pubsForGroup.map(pub => {
                    const isPdfPublication = !!pub.bodyPdf
                    const link = `${baseRoute}${generatePublicationLink({ isPdfPublication, isFolder, resourceKey: pub.resourceKey })}`
                    const label = isPdfPublication ? 'PDF' : 'View'
                    return (
                      <Link key={pub.resourceKey} to={link} className={`nav-button ${!isPdfPublication ? 'view-button' : ''}`} onClick={() => { onClick && onClick(topic) }}>
                        {isPdfPublication && <DownloadIcon className='download-icon' />}
                        {label}
                      </Link>
                    )
                  })}
                </div>
                <div className='title-icon'>
                  <div className="item-iconbar">
                    {hasVideo && <Icon type="list-film" />}
                    {hasImage && <Icon type="list-image" />}
                    {isPublication && <Icon type={publicationIcon} />}
                    {!isFolder && !isPublication && !hasVideo && !hasImage && <Icon type="list-topic" />}
                    {!isDesktop && isFolder && <Icon type="arrow-left" />}
                    {isDesktop && isFolder && <Icon type="folder" />}
                  </div>
                  <div className="item-title">
                    {title}
                  </div>
                </div>
              </div>
            </li>
          )
        }
      } else {
        // non-publication topics remain unchanged (with Link wrapper)
        const isPdfPublication = publicationsList
          .some(publication => publication.resourceKey === resourceKey && !!publication.bodyPdf)

        let link = `${baseRoute}${generatePublicationLink({ isPdfPublication, isFolder, resourceKey })}`

        topicElements.push(
          <li key={key + 1}>
            <Link className="item-inner"
              to={link}
              onClick={() => { localStorage.setItem('currentTopicId', topic.get('id')); onClick && onClick(topic) }}>
              <div className="item-title">{topic.get('title')}</div>
              <div className="item-iconbar">
                {hasVideo && <Icon type="list-film" />}
                {hasImage && <Icon type="list-image" />}
                {!isFolder && !isPublication && !hasVideo && !hasImage && <Icon type="list-topic" />}
                {!isDesktop && isFolder && <Icon type="arrow-left" />}
                {isDesktop && isFolder && <Icon type="folder" />}
              </div>
            </Link>
          </li>
        )
      }

      publicationTypes[publicationType] = Array.isArray(publicationTypes[publicationType]) ? [...publicationTypes[publicationType], index] : [index]
      ++index
      return true
    })

    let orderedTopicElements = []
    if (publicationTypes['QRG']) {
      publicationTypes['QRG'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['QRG']
    }
    if (publicationTypes['OM']) {
      publicationTypes['OM'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['OM']
    }
    if (publicationTypes['NM']) {
      publicationTypes['NM'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['NM']
    }
    if (publicationTypes['UG']) {
      publicationTypes['UG'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['UG']
    }
    const otherPubs = Object.keys(publicationTypes)
    if (otherPubs?.length) {
      otherPubs.forEach(pub => {
        publicationTypes[pub].forEach(index => orderedTopicElements.push(topicElements[index]))
      })
    }

    return orderedTopicElements

  }, [topics, publications])

  if (topics === null || topics.size === 0) {
    return <Loader type="status" className="inverse">{emptyText}</Loader>
  }

  return (
    <ul className={classNames('TopicsList', className)}>
      {topicElements}
    </ul>
  )
}

TopicsList.propTypes = {
  topics: PropTypes.oneOfType([ImmutablePropTypes.list, ImmutablePropTypes.map]),
  publications: ImmutablePropTypes.list,
  onBookmarkClick: PropTypes.func,
  referrer: PropTypes.oneOf([ROUTE.BROWSE, ROUTE.FAVORITES, ROUTE.TOPPICKS, ROUTE.EXPLORE, ROUTE.SEARCH]),
  emptyText: PropTypes.string,
  baseRoute: PropTypes.string.isRequired,
  className: PropTypes.string,
  isDesktop: PropTypes.bool,
  onClick: PropTypes.func,
  allTopics: ImmutablePropTypes.list,
  contentIndex: PropTypes.shape({
    publicationType: PropTypes.string
  })
}

const mapStateToProps = (state) => ({
  publications: state.contents.publications,
  allTopics: state.contents.topics,
  contentIndex: state.contents.contentIndex,
})

export default connect(mapStateToProps)(TopicsList)
